'use client'

import { useEffect, useState } from 'react'

import { Loader2 } from 'lucide-react'
import { validateCPF } from 'validations-br'

import type { Mission } from '@/features/missions/types/mission'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import { inputClassName } from '@/shared/ui/input-styles'

interface RegistrationNumberFormProps {
  mission: Mission
  onSuccess?: () => void
  onError?: (errors: Record<string, string[]>) => void
}

const labelClassName = 'block text-sm font-medium text-gray-700 mb-1'

function maskCPF(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export function RegistrationNumberForm({
  mission: _mission,
  onSuccess,
  onError
}: RegistrationNumberFormProps) {
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  useEffect(() => {
    let active = true
    api
      .get('/user')
      .then(({ data }) => {
        if (!active) return
        const current = data?.record?.registration_number
        if (current) setRegistrationNumber(maskCPF(String(current)))
      })
      .catch(() => {})
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    if (!registrationNumber || !validateCPF(registrationNumber)) {
      setFieldErrors({ registration_number: ['Informe um CPF válido'] })
      return
    }

    setFieldErrors({})
    setIsSubmitting(true)
    try {
      await api.patch('/user/registration_data', {
        record: { registration_number: registrationNumber }
      })
      onSuccess?.()
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'response' in err
          ? (
              err as {
                response?: { data?: { errors?: Record<string, string[]> } }
              }
            ).response?.data
          : undefined
      const errors = data?.errors ?? {
        _base: ['Não foi possível atualizar. Tente novamente.']
      }
      setFieldErrors(errors)
      onError?.(errors)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label htmlFor="registration-number" className={labelClassName}>
          CPF
        </label>
        <input
          id="registration-number"
          type="text"
          inputMode="numeric"
          placeholder="000.000.000-00"
          value={registrationNumber}
          onChange={e => setRegistrationNumber(maskCPF(e.target.value))}
          className={cn(
            inputClassName,
            fieldErrors.registration_number && 'border-red-500'
          )}
          disabled={isLoading}
          required
        />
        {fieldErrors.registration_number?.length ? (
          <p className="mt-1 text-sm text-red-600">
            {fieldErrors.registration_number[0]}
          </p>
        ) : null}
      </div>

      {fieldErrors._base?.length ? (
        <p className="text-sm text-red-600">{fieldErrors._base[0]}</p>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting || isLoading}
        className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Salvando...
          </>
        ) : (
          'Salvar CPF'
        )}
      </button>
    </form>
  )
}
